/**
 * API响应数据模型
 * 匹配后端 agent.common.utils.Result<T> 结构
 */

/**
 * 通用API响应格式
 */
export class ApiResponse extends Error {
  constructor(data = null, code = 0, msg = 'success') {
    super(msg || '未知错误')
    this.name = 'ApiResponse'
    this.code = code
    this.msg = msg
    this.data = data
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiResponse)
    }
  }

  get message() {
    return this.msg
  }

  set message(val) {
    this.msg = val
  }

  toString() {
    return this.msg || `ApiResponse(code=${this.code})`
  }

  /**
   * 判断是否成功
   * @returns {boolean}
   */
  isSuccess() {
    return this.code === 0
  }

  /**
   * 判断是否失败
   * @returns {boolean}
   */
  isError() {
    return this.code !== 0
  }

  /**
   * 获取错误信息（支持国际化）
   * @param {Object} $t - 国际化函数
   * @returns {string}
   */
  getErrorMessage($t = null) {
    const defaultMessage = this.msg || '未知错误'
    
    // 检查国际化函数是否可用且为函数类型
    if ($t && typeof $t === 'function' && this.code) {
      try {
        const i18nKey = `errorCodes.${this.code}`
        const translated = $t(i18nKey)
        // 如果翻译结果与键名不同，说明翻译成功
        if (translated !== i18nKey) {
          return translated
        }
      } catch (error) {
        console.warn('国际化函数调用失败:', error)
        // 如果国际化调用失败，使用默认消息
      }
    }
    
    return defaultMessage
  }

  /**
   * 获取错误类型
   * @returns {string}
   */
  getErrorType() {
    if (this.code >= 20001 && this.code <= 20008) {
      return 'AUTH'
    } else if (this.code >= 30001 && this.code <= 30006) {
      return 'CAPTCHA'
    } else if (this.code >= 40001 && this.code <= 40008) {
      return 'USER'
    } else if (this.code >= 50001 && this.code <= 50004) {
      return 'BUSINESS'
    } else if (this.code >= 60001 && this.code <= 60004) {
      return 'SYSTEM'
    } else {
      return 'UNKNOWN'
    }
  }

  /**
   * 判断是否需要重新登录
   * @returns {boolean}
   */
  needReLogin() {
    return this.code >= 20001 && this.code <= 20008
  }

  /**
   * 判断是否是网络错误
   * @returns {boolean}
   */
  isNetworkError() {
    return this.code >= 60001 && this.code <= 60004
  }
}

/**
 * 分页响应数据模型
 */
export class PageResponse {
  constructor(records = [], total = 0, page = 1, pageSize = 10) {
    this.records = records
    this.total = total
    this.page = page
    this.pageSize = pageSize
    this.pages = Math.ceil(total / pageSize)
  }
}

/**
 * 用户登录响应数据模型
 */
export class LoginResponse {
  constructor(token = '', userInfo = {}, menus = [], permissions = []) {
    this.token = token
    this.userInfo = userInfo
    this.menus = menus
    this.permissions = permissions
  }
}

/**
 * 用户信息数据模型
 */
export class UserInfo {
  constructor(userId = '', username = '', name = '', email = '', phone = '', avatar = '') {
    this.userId = userId
    this.username = username
    this.name = name
    this.email = email
    this.phone = phone
    this.avatar = avatar
  }
}

/**
 * 菜单数据模型
 */
export class MenuInfo {
  constructor(menuId = '', menuName = '', path = '', component = '', icon = '', parentId = '') {
    this.menuId = menuId
    this.menuName = menuName
    this.path = path
    this.component = component
    this.icon = icon
    this.parentId = parentId
  }
}

/**
 * 权限数据模型
 */
export class PermissionInfo {
  constructor(permissionId = '', permissionName = '', resource = '', action = '') {
    this.permissionId = permissionId
    this.permissionName = permissionName
    this.resource = resource
    this.action = action
  }
}

/**
 * 角色数据模型
 */
export class RoleInfo {
  constructor(id = '', roleName = '', roleCode = '', description = '') {
    this.id = id
    this.roleName = roleName
    this.roleCode = roleCode
    this.description = description
  }
}

/**
 * 错误码定义
 */
export const ErrorCodes = {
  // 系统错误码 (10000-19999)
  SUCCESS: 0,
  INTERNAL_SERVER_ERROR: 10000,
  PARAMETER_ERROR: 10001,
  PARAMETER_MISSING: 10002,
  PARAMETER_TYPE_ERROR: 10003,
  DATA_NOT_FOUND: 10004,
  DATA_ALREADY_EXISTS: 10005,
  DATA_INTEGRITY_VIOLATION: 10006,

  // 认证错误码 (20000-29999)
  UNAUTHORIZED: 20001,
  TOKEN_INVALID: 20002,
  TOKEN_EXPIRED: 20003,
  TOKEN_MALFORMED: 20004,
  ACCESS_DENIED: 20005,
  ACCOUNT_DISABLED: 20006,
  ACCOUNT_LOCKED: 20007,
  PASSWORD_ERROR: 20008,

  // 验证码错误码 (30000-39999)
  CAPTCHA_ERROR: 30001,
  CAPTCHA_EXPIRED: 30002,
  CAPTCHA_NOT_FOUND: 30003,
  CAPTCHA_MISMATCH: 30004,
  SMS_CODE_ERROR: 30005,
  SMS_CODE_EXPIRED: 30006,
  SMS_CAPTCHA_ERROR: 10067,

  // 用户相关错误码 (40000-49999)
  USER_NOT_FOUND: 40001,
  USER_ALREADY_EXISTS: 40002,
  USERNAME_ERROR: 40003,
  PASSWORD_ERROR: 40004,
  PASSWORD_WEAK: 40005,
  PASSWORD_TOO_SHORT: 40006,
  EMAIL_ERROR: 40007,
  PHONE_ERROR: 40008,

  // 业务逻辑错误码 (50000-59999)
  OPERATION_NOT_ALLOWED: 50001,
  OPERATION_FAILED: 50002,
  DATA_VALIDATION_FAILED: 50003,
  BUSINESS_LOGIC_ERROR: 50004,

  // 外部服务错误码 (60000-69999)
  EXTERNAL_SERVICE_ERROR: 60001,
  DATABASE_ERROR: 60002,
  NETWORK_ERROR: 60003,
  TIMEOUT_ERROR: 60004
}

/**
 * 错误码映射到错误信息
 */
export const ErrorMessages = {
  [ErrorCodes.SUCCESS]: '操作成功',
  [ErrorCodes.INTERNAL_SERVER_ERROR]: '服务器内部错误',
  [ErrorCodes.PARAMETER_ERROR]: '参数错误',
  [ErrorCodes.PARAMETER_MISSING]: '缺少必要参数',
  [ErrorCodes.PARAMETER_TYPE_ERROR]: '参数类型错误',
  [ErrorCodes.DATA_NOT_FOUND]: '数据不存在',
  [ErrorCodes.DATA_ALREADY_EXISTS]: '数据已存在',
  [ErrorCodes.DATA_INTEGRITY_VIOLATION]: '数据完整性违反',

  [ErrorCodes.UNAUTHORIZED]: '未授权访问',
  [ErrorCodes.TOKEN_INVALID]: '令牌无效',
  [ErrorCodes.TOKEN_EXPIRED]: '令牌已过期',
  [ErrorCodes.TOKEN_MALFORMED]: '令牌格式错误',
  [ErrorCodes.ACCESS_DENIED]: '访问被拒绝',
  [ErrorCodes.ACCOUNT_DISABLED]: '账户已被禁用',
  [ErrorCodes.ACCOUNT_LOCKED]: '账户已被锁定',
  [ErrorCodes.PASSWORD_ERROR]: '密码错误',

  [ErrorCodes.CAPTCHA_ERROR]: '验证码错误，请重新输入',
  [ErrorCodes.CAPTCHA_EXPIRED]: '验证码已过期，请重新获取',
  [ErrorCodes.CAPTCHA_NOT_FOUND]: '验证码不存在，请重新获取',
  [ErrorCodes.CAPTCHA_MISMATCH]: '验证码不匹配，请重新输入',
  [ErrorCodes.SMS_CODE_ERROR]: '短信验证码错误',
  [ErrorCodes.SMS_CODE_EXPIRED]: '短信验证码已过期',
  [ErrorCodes.SMS_CAPTCHA_ERROR]: '验证码错误，请重新输入',

  [ErrorCodes.USER_NOT_FOUND]: '用户不存在',
  [ErrorCodes.USER_ALREADY_EXISTS]: '用户已存在',
  [ErrorCodes.USERNAME_ERROR]: '用户名错误',
  [ErrorCodes.PASSWORD_ERROR]: '密码错误',
  [ErrorCodes.PASSWORD_WEAK]: '密码强度不足',
  [ErrorCodes.PASSWORD_TOO_SHORT]: '密码长度不足',
  [ErrorCodes.EMAIL_ERROR]: '邮箱格式错误',
  [ErrorCodes.PHONE_ERROR]: '手机号格式错误',

  [ErrorCodes.OPERATION_NOT_ALLOWED]: '操作不被允许',
  [ErrorCodes.OPERATION_FAILED]: '操作失败',
  [ErrorCodes.DATA_VALIDATION_FAILED]: '数据验证失败',
  [ErrorCodes.BUSINESS_LOGIC_ERROR]: '业务逻辑错误',

  [ErrorCodes.EXTERNAL_SERVICE_ERROR]: '外部服务错误',
  [ErrorCodes.DATABASE_ERROR]: '数据库错误',
  [ErrorCodes.NETWORK_ERROR]: '网络错误',
  [ErrorCodes.TIMEOUT_ERROR]: '请求超时'
}

/**
 * 根据错误码获取错误信息
 * @param {number} code - 错误码
 * @returns {string}
 */
export function getErrorMessage(code) {
  return ErrorMessages[code] || `未知错误 (${code})`
}

/**
 * 检查响应是否成功
 * @param {Object} response - API响应
 * @returns {boolean}
 */
export function isSuccessResponse(response) {
  return response && response.code === 0
}

/**
 * 检查响应是否失败
 * @param {Object} response - API响应
 * @returns {boolean}
 */
export function isErrorResponse(response) {
  return response && response.code !== 0
}

/**
 * 获取响应数据
 * @param {Object} response - API响应
 * @returns {*}
 */
export function getResponseData(response) {
  return response && response.data !== undefined ? response.data : response
}

/**
 * 处理API响应
 * @param {Object} response - API响应
 * @returns {ApiResponse}
 */
export function handleResponse(response) {
  if (!response) {
    return new ApiResponse(null, ErrorCodes.NETWORK_ERROR, '网络响应为空')
  }

  const { code, msg, data } = response
  
  // 增强处理：如果 data 为空，但 response 中有其他业务字段（排除 code 和 msg），
  // 则尝试使用这些字段作为 data。这兼容了后端 R.ok().put("user", user) 这种写法。
  let finalData = data
  if (finalData === undefined) {
    const otherKeys = Object.keys(response).filter(k => k !== 'code' && k !== 'msg')
    if (otherKeys.length === 1) {
      // 只有一个业务字段，直接作为 data 返回
      finalData = response[otherKeys[0]]
    } else if (otherKeys.length > 1) {
      // 有多个业务字段，或者没有业务字段但有 code/msg，返回整个 response 供调用方处理
      finalData = response
    }
  }

  return new ApiResponse(finalData, code, msg)
}

/**
 * 处理API错误
 * @param {Error} error - 错误对象
 * @returns {ApiResponse}
 */
export function handleError(error) {
  if (process.env.NODE_ENV === 'development') {
    console.debug('[API Error]', error?.message || error)
  }

  if (error.response) {
    // 服务器响应错误
    const { status, data } = error.response
    
    // 特殊处理 Blob 类型的错误响应
    if (data instanceof Blob && data.type === 'application/json') {
      // 这种情况下，具体的错误信息需要异步读取，我们在拦截器中处理
      return new ApiResponse(null, ErrorCodes.INTERNAL_SERVER_ERROR, '服务器响应错误 (Blob)')
    }

    const code = data?.code || ErrorCodes.INTERNAL_SERVER_ERROR
    const message = data?.msg || (new ApiResponse(null, code)).getErrorMessage()
    return new ApiResponse(null, code, message)
  } else if (error.request) {
    // 网络错误
    return new ApiResponse(null, ErrorCodes.NETWORK_ERROR, '网络连接失败')
  } else {
    // 其他错误
    return new ApiResponse(null, ErrorCodes.INTERNAL_SERVER_ERROR, error.message || '未知错误')
  }
}

/**
 * 显示错误消息的工具函数
 * @param {Error|ApiResponse} error - 错误对象
 * @param {Object} $message - Element UI的Message组件
 * @param {Function} $t - 国际化函数
 */
export function showErrorMessage(error, $message, $t = null) {
  let apiResponse
  
  if (error instanceof ApiResponse) {
    apiResponse = error
  } else {
    apiResponse = handleError(error)
  }
  
  const message = apiResponse.getErrorMessage($t)
  const errorType = apiResponse.getErrorType()
  
  // 根据错误类型选择消息类型
  let messageType = 'error'
  let duration = 5000
  
  switch (errorType) {
    case 'AUTH':
      duration = 7000 // 认证错误显示时间更长
      break
    case 'CAPTCHA':
      duration = 3000 // 验证码错误显示时间较短
      break
    case 'BUSINESS':
      duration = 6000 // 业务错误中等显示时间
      break
    case 'SYSTEM':
      duration = 7000 // 系统错误显示时间较长
      break
  }
  
  // 检查 $message 是否是有效函数
  if ($message && typeof $message === 'function') {
    $message({
      message,
      type: messageType,
      duration
    })
  } else {
    console.warn('[API]', message)
  }
  
  return apiResponse
}

/**
 * 智能重试机制
 * @param {Function} apiFunction - API调用函数
 * @param {Object} options - 重试选项
 * @returns {Promise}
 */
export async function smartRetry(apiFunction, options = {}) {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 10000,
    retryCondition = (error) => {
      // 默认重试条件：网络错误、超时错误
      const apiResponse = handleError(error)
      return apiResponse.isNetworkError() || apiResponse.code === ErrorCodes.TIMEOUT_ERROR
    }
  } = options
  
  let lastError
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await apiFunction()
    } catch (error) {
      lastError = error
      
      // 如果是最后一次尝试或者不满足重试条件，直接抛出错误
      if (attempt === maxRetries || !retryCondition(error)) {
        throw error
      }
      
      // 计算延迟时间（指数退避 + 随机抖动）
      const delay = Math.min(
        baseDelay * Math.pow(2, attempt) + Math.random() * 1000,
        maxDelay
      )
      
      console.warn(`API调用失败，${delay}ms后进行第${attempt + 1}次重试:`, error.message)
      
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
  
  throw lastError
}

/**
 * 错误日志记录
 * @param {Error|ApiResponse} error - 错误对象
 * @param {Object} context - 上下文信息
 */
export function logError(error, context = {}) {
  const timestamp = new Date().toISOString()
  const apiResponse = error instanceof ApiResponse ? error : handleError(error)
  
  const logData = {
    timestamp,
    errorCode: apiResponse.code,
    errorMessage: apiResponse.getErrorMessage(),
    errorType: apiResponse.getErrorType(),
    url: context.url,
    method: context.method,
    userAgent: navigator.userAgent,
    currentUrl: window.location.href,
    stack: error.stack
  }
  
  if (process.env.NODE_ENV === 'development') {
    console.debug(`[API Error ${timestamp}]`, error.msg || error.message)
  }
  
  // 这里可以添加错误上报逻辑
  // 比如发送到错误监控服务
}

/**
 * 网络状态检测
 * @returns {Promise<boolean>}
 */
export function checkNetworkStatus() {
  return new Promise((resolve) => {
    if (!navigator.onLine) {
      resolve(false)
      return
    }
    
    // 尝试连接到一个可靠的外部服务
    fetch('https://httpbin.org/status/200', {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-cache'
    })
    .then(() => resolve(true))
    .catch(() => resolve(false))
  })
}

/**
 * 批量错误处理
 * @param {Array} promises - Promise数组
 * @param {Function} onError - 错误处理函数
 * @returns {Promise<Array>}
 */
export function handleBatchErrors(promises, onError = null) {
  return Promise.allSettled(promises).then(results => {
    const errors = []
    const successes = []
    
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        successes.push(result.value)
      } else {
        const error = result.reason
        errors.push({ index, error })
        
        if (onError) {
          onError(error, index)
        } else if (process.env.NODE_ENV === 'development') {
          console.debug(`[Batch] Item ${index + 1} failed:`, error?.msg || error?.message || error)
        }
      }
    })
    
    return { successes, errors }
  })
}